import React from "react";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import FormatTextdirectionLToRIcon from "@mui/icons-material/FormatTextdirectionLToR";
import FormatTextdirectionRToLIcon from "@mui/icons-material/FormatTextdirectionRToL";
import { useEditorState } from "@tiptap/react";
import {
  MenuButton,
  MenuButtonBold,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonRemoveFormatting,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuSelectHeading,
  MenuSelectTextAlign,
  useRichTextEditorContext
} from "mui-tiptap";

import { useContentContext } from "./content-context";

const menuControlsContainerSx = {
  background: "white !important",
  // Counteract parent menuBarContent padding (4px 0)
  margin: "-4px 0",
  // padding: "10px 0",

  ".MuiSelect-select": {
    padding: "2px 20px 2px 6px !important",
    color: "#071f35"
  },

  "& .MuiButtonBase-root": {
    color: "#071f35"
  },

  "& .MuiButtonBase-root svg": {
    color: "#071f35"
  }
};

export const TextSectionToolbar: React.FC = () => {
  const editor = useRichTextEditorContext();
  const { currentSectionId, setCurrentSectionIsRtl } = useContentContext();

  // Force re-render when editor state changes (cursor move, selection, etc.)
  useEditorState({
    editor,
    selector: (snapshot) => snapshot.transactionNumber
  });

  const handleSetTextDirection = (direction: "ltr" | "rtl") => {
    if (currentSectionId && editor) {
      // Update UI state — the textDirection prop on RichTextEditor handles the visual change
      setCurrentSectionIsRtl(direction === "rtl");
      // Persist to database (single API call)
      editor.chain().focus().setTextDirection(direction).run();
      // updateSection({ id: currentSectionId, is_rtl: direction === "rtl" });
    }
  };

  return (
    <MenuControlsContainer sx={menuControlsContainerSx}>
      <MenuButtonBold />
      <MenuButtonItalic />
      <MenuButtonUnderline />
      <MenuSelectHeading />
      <MenuSelectTextAlign
        sx={{
          ".MuiSvgIcon-root": {
            fill: "#071f35 !important"
          }
        }}
        emptyLabel={<FormatAlignLeftIcon />}
      />
      <MenuButton
        tooltipLabel="Ordered list"
        tooltipShortcutKeys={["mod", "Shift", "7"]}
        IconComponent={FormatListNumbered}
        selected={editor?.isActive("orderedList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
      />
      <MenuButton
        tooltipLabel="Bulleted list"
        tooltipShortcutKeys={["mod", "Shift", "8"]}
        IconComponent={FormatListBulleted}
        selected={editor?.isActive("bulletList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
      />
      <MenuButtonEditLink />
      <MenuButton
        tooltipLabel="Left to right"
        IconComponent={FormatTextdirectionLToRIcon}
        selected={editor?.isActive({ dir: "ltr" }) ?? false}
        disabled={!editor?.isEditable || !editor.can().setTextDirection("ltr")}
        onClick={() => handleSetTextDirection("ltr")}
      />
      <MenuButton
        tooltipLabel="Right to left"
        IconComponent={FormatTextdirectionRToLIcon}
        selected={editor?.isEditable && (editor?.isActive({ dir: "rtl" }) ?? false)}
        disabled={!editor?.isEditable || !editor.can().setTextDirection("rtl")}
        onClick={() => handleSetTextDirection("rtl")}
      />
      <MenuButtonRemoveFormatting />
    </MenuControlsContainer>
  );
};
